import {
  createShopApiGraphqlClient,
  type ShopApiGraphqlClient,
} from "../../../packages/pvc-product/shop-api-transport";
import {
  ISRAELI_BUSINESS_ID_ERROR,
  ISRAELI_PHONE_ERROR,
  normalizeIsraeliPhone,
  normalizeValidIsraeliBusinessId,
} from "./customer-validation";

export type CustomerKind = "private" | "business";

export type AccountAddress = {
  id: string;
  fullName: string | null;
  company: string | null;
  streetLine1: string;
  streetLine2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  countryCode: string;
  countryName: string;
  phoneNumber: string | null;
  defaultShippingAddress: boolean;
  defaultBillingAddress: boolean;
};

export type AccountSettings = {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string | null;
  customerType: CustomerKind;
  businessName: string | null;
  businessId: string | null;
  addresses: AccountAddress[];
};

export type UpdateAccountProfileInput = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  customerType: CustomerKind;
  businessName?: string;
  businessId?: string;
};

export type AccountAddressInput = {
  fullName: string;
  company?: string;
  streetLine1: string;
  streetLine2?: string;
  city: string;
  province?: string;
  postalCode?: string;
  phoneNumber?: string;
  defaultShippingAddress: boolean;
  defaultBillingAddress: boolean;
};

export class AccountSettingsDomainError extends Error {
  constructor(public readonly errorCode: string, message: string) {
    super(message);
    this.name = "AccountSettingsDomainError";
  }
}

const SETTINGS_FIELDS = `
  id
  firstName
  lastName
  emailAddress
  phoneNumber
  customFields {
    customerType
    businessName
    businessId
  }
  addresses {
    id
    fullName
    company
    streetLine1
    streetLine2
    city
    province
    postalCode
    country { code name }
    phoneNumber
    defaultShippingAddress
    defaultBillingAddress
  }
`;

const LOAD_SETTINGS = `
  query HappyPrintAccountSettings {
    activeCustomer { ${SETTINGS_FIELDS} }
  }
`;

const UPDATE_PROFILE = `
  mutation HappyPrintUpdateCustomer($input: UpdateCustomerInput!) {
    updateCustomer(input: $input) { ${SETTINGS_FIELDS} }
  }
`;

const CREATE_ADDRESS = `
  mutation HappyPrintCreateCustomerAddress($input: CreateAddressInput!) {
    createCustomerAddress(input: $input) { id }
  }
`;

const UPDATE_ADDRESS = `
  mutation HappyPrintUpdateCustomerAddress($input: UpdateAddressInput!) {
    updateCustomerAddress(input: $input) { id }
  }
`;

const DELETE_ADDRESS = `
  mutation HappyPrintDeleteCustomerAddress($id: ID!) {
    deleteCustomerAddress(id: $id) { success }
  }
`;

function object(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function string(value: unknown, path: string): string {
  if (typeof value !== "string") throw new Error(`${path} must be a string`);
  return value;
}

function nullableString(value: unknown, path: string): string | null {
  if (value == null) return null;
  if (typeof value !== "string") throw new Error(`${path} must be a string or null`);
  return value || null;
}

function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${path} must be a boolean`);
  return value;
}

function decodeAddress(value: unknown, path: string): AccountAddress {
  const address = object(value, path);
  const country = object(address.country, `${path}.country`);
  return {
    id: string(address.id, `${path}.id`),
    fullName: nullableString(address.fullName, `${path}.fullName`),
    company: nullableString(address.company, `${path}.company`),
    streetLine1: string(address.streetLine1, `${path}.streetLine1`),
    streetLine2: nullableString(address.streetLine2, `${path}.streetLine2`),
    city: nullableString(address.city, `${path}.city`),
    province: nullableString(address.province, `${path}.province`),
    postalCode: nullableString(address.postalCode, `${path}.postalCode`),
    countryCode: string(country.code, `${path}.country.code`),
    countryName: string(country.name, `${path}.country.name`),
    phoneNumber: nullableString(address.phoneNumber, `${path}.phoneNumber`),
    defaultShippingAddress: boolean(address.defaultShippingAddress, `${path}.defaultShippingAddress`),
    defaultBillingAddress: boolean(address.defaultBillingAddress, `${path}.defaultBillingAddress`),
  };
}

function decodeSettings(value: unknown): AccountSettings | null {
  if (value == null) return null;
  const customer = object(value, "data.activeCustomer");
  const customFields = customer.customFields == null
    ? {}
    : object(customer.customFields, "data.activeCustomer.customFields");
  if (!Array.isArray(customer.addresses)) throw new Error("data.activeCustomer.addresses must be an array");
  return {
    id: string(customer.id, "data.activeCustomer.id"),
    firstName: string(customer.firstName, "data.activeCustomer.firstName"),
    lastName: string(customer.lastName, "data.activeCustomer.lastName"),
    emailAddress: string(customer.emailAddress, "data.activeCustomer.emailAddress"),
    phoneNumber: nullableString(customer.phoneNumber, "data.activeCustomer.phoneNumber"),
    customerType: customFields.customerType === "business" ? "business" : "private",
    businessName: nullableString(customFields.businessName, "data.activeCustomer.customFields.businessName"),
    businessId: nullableString(customFields.businessId, "data.activeCustomer.customFields.businessId"),
    addresses: customer.addresses.map((address, index) => decodeAddress(address, `data.activeCustomer.addresses[${index}]`)),
  };
}

function toAddressInput(input: AccountAddressInput) {
  const streetLine1 = input.streetLine1.trim();
  const city = input.city.trim();
  if (!streetLine1 || !city) {
    throw new AccountSettingsDomainError("INVALID_ADDRESS", "יש למלא רחוב ומספר ועיר.");
  }

  const rawPhone = input.phoneNumber?.trim() ?? "";
  const phoneNumber = rawPhone ? normalizeIsraeliPhone(rawPhone) : null;
  if (rawPhone && !phoneNumber) {
    throw new AccountSettingsDomainError("INVALID_ISRAELI_PHONE", ISRAELI_PHONE_ERROR);
  }

  return {
    fullName: input.fullName.trim() || null,
    company: input.company?.trim() || null,
    streetLine1,
    streetLine2: input.streetLine2?.trim() || null,
    city,
    province: input.province?.trim() || null,
    postalCode: input.postalCode?.trim() || null,
    countryCode: "IL",
    phoneNumber,
    defaultShippingAddress: input.defaultShippingAddress,
    defaultBillingAddress: input.defaultBillingAddress,
  };
}

export function createAccountSettingsApi(client: ShopApiGraphqlClient) {
  const load = async (signal?: AbortSignal): Promise<AccountSettings | null> => {
    const raw = await client.execute({ operationName: "HappyPrintAccountSettings", document: LOAD_SETTINGS, signal });
    const data = object(raw, "data");
    return decodeSettings(data.activeCustomer);
  };

  return {
    load,

    async updateProfile(input: UpdateAccountProfileInput): Promise<AccountSettings> {
      const firstName = input.firstName.trim();
      const lastName = input.lastName.trim();
      if (!firstName || !lastName) {
        throw new AccountSettingsDomainError("INVALID_NAME", "יש למלא שם פרטי ושם משפחה.");
      }

      const phoneNumber = normalizeIsraeliPhone(input.phoneNumber);
      if (!phoneNumber) {
        throw new AccountSettingsDomainError("INVALID_ISRAELI_PHONE", ISRAELI_PHONE_ERROR);
      }

      const business = input.customerType === "business";
      const businessName = business ? input.businessName?.trim() || "" : "";
      const businessId = business ? normalizeValidIsraeliBusinessId(input.businessId ?? "") : null;
      if (business && !businessName) {
        throw new AccountSettingsDomainError("BUSINESS_NAME_REQUIRED", "לחשבון עסקי יש למלא שם עסק.");
      }
      if (business && !businessId) {
        throw new AccountSettingsDomainError("INVALID_ISRAELI_BUSINESS_ID", ISRAELI_BUSINESS_ID_ERROR);
      }

      const raw = await client.execute({
        operationName: "HappyPrintUpdateCustomer",
        document: UPDATE_PROFILE,
        variables: {
          input: {
            firstName,
            lastName,
            phoneNumber,
            customFields: {
              customerType: input.customerType,
              businessName: business ? businessName : null,
              businessId: business ? businessId : null,
            },
          },
        },
      });
      const data = object(raw, "data");
      const settings = decodeSettings(data.updateCustomer);
      if (!settings) throw new Error("updateCustomer returned no customer");
      return settings;
    },

    async createAddress(input: AccountAddressInput): Promise<AccountSettings | null> {
      await client.execute({
        operationName: "HappyPrintCreateCustomerAddress",
        document: CREATE_ADDRESS,
        variables: { input: toAddressInput(input) },
      });
      return load();
    },

    async updateAddress(id: string, input: AccountAddressInput): Promise<AccountSettings | null> {
      await client.execute({
        operationName: "HappyPrintUpdateCustomerAddress",
        document: UPDATE_ADDRESS,
        variables: { input: { id, ...toAddressInput(input) } },
      });
      return load();
    },

    async deleteAddress(id: string): Promise<AccountSettings | null> {
      await client.execute({
        operationName: "HappyPrintDeleteCustomerAddress",
        document: DELETE_ADDRESS,
        variables: { id },
      });
      return load();
    },
  };
}

export function createBrowserAccountSettingsApi() {
  const endpoint = import.meta.env.VITE_SHOP_API_URL || "/shop-api";
  return createAccountSettingsApi(createShopApiGraphqlClient({ endpoint }));
}
